/**
 * Tool panel dispatch.
 *
 *   registry id → panel component → rendered in the TOC's tool tab
 *
 * Adding a tool means a registry entry, a panel, and one line here. Before
 * this file the TOC carried eight panels inline and the registry advertised
 * twenty, so twelve tools could be added from the Tool Library and opened onto
 * an empty tab. `hasToolPanel` is what stops that from recurring: the Tool
 * Library asks it before offering a tool at all.
 */

import type { ReactElement } from 'react';
import type { ToolContext } from './types';
import { BasemapPanel, CoordinatesPanel, DirectionsPanel, LegendPanel } from './NavigatePanels';
import { AttributeTablePanel, BufferPanel, IdentifyPanel } from './QueryPanels';
import { AddDataPanel, EditPanel, SwipePanel } from './EditPanels';
import { ExportKmlPanel, ReadoutPanel, SharePanel } from './ReadoutSharePanels';
import { ReportPanel } from './ReportPanel';
import { TracePanel } from './TracePanel';
import { ClipPanel } from './ClipPanel';
import { DigitizePanel } from './DigitizePanel';
import { GeoreferencePanel } from './GeoreferencePanel';
import { SearchPanel } from './SearchPanel';
import { SelectByLocationPanel } from './SelectByLocationPanel';
import { DensityPanel } from './DensityPanel';
import { JoinFeaturesPanel } from './JoinFeaturesPanel';
import { FindProblemsPanel } from './FindProblemsPanel';
import { TimeSliderPanel } from './TimeSliderPanel';

export type { ToolContext, BasemapId, MapPoint } from './types';

type PanelComponent = (props: { ctx: ToolContext }) => ReactElement;

/**
 * Panels that live in this folder.
 *
 * The eight older tools (draw, print, measure, filter, query, select,
 * assetSearch, bookmark) are still rendered inline by TableOfContents and are
 * deliberately absent here — `hasToolPanel` below accounts for both.
 */
const PANELS: Record<string, PanelComponent> = {
  search: SearchPanel,
  identify: IdentifyPanel,
  attributeTable: AttributeTablePanel,
  buffer: BufferPanel,
  density: DensityPanel,
  joinFeatures: JoinFeaturesPanel,
  findProblems: FindProblemsPanel,
  timeSlider: TimeSliderPanel,
  selectLocation: SelectByLocationPanel,
  coordinates: CoordinatesPanel,
  basemapGallery: BasemapPanel,
  legend: LegendPanel,
  direction: DirectionsPanel,
  elevation: ReadoutPanel,
  addData: AddDataPanel,
  edit: EditPanel,
  swipe: SwipePanel,
  share: SharePanel,
  exportKml: ExportKmlPanel,
  report: ReportPanel,
  trace: TracePanel,
  clip: ClipPanel,
  digitize: DigitizePanel,
  georeference: GeoreferencePanel,
};

/**
 * Tools that take the map's clicks for themselves.
 *
 * While one of these is the open tab, a click means something to IT — a vertex,
 * a corner, a coordinate to read — and the identify pop-up must stay out of the
 * way. Two things answering the same click is the bug this list prevents:
 * dropping a sketch vertex should not also open a card over the point you are
 * aiming at.
 *
 * Sketching tools (Draw, Measure, Select) are NOT here: they are recognised by
 * `drawMode` being set, which is a stronger signal — it is true only while the
 * sketch is actually armed, so identify keeps working from their panel until
 * someone presses Start.
 */
export const CLICK_OWNING_TOOL_IDS = [
  // Reads the clicked point and lists what is under it — a pop-up saying the
  // same thing beside it is duplication, not confirmation.
  'identify',
  // Reads the click as a coordinate, and drops pins on it.
  'coordinates',
  'elevation',
  // Drops the incident flag on the clicked point.
  'assetSearch',
  // The click chooses the pipe the trace starts from.
  'trace',
  // Reads the click as "select this feature to edit" — a pop-up over the
  // same feature would be a second, conflicting answer to the same click.
  'edit',
  // Reads the click as a control point's real-world position — a pop-up
  // over the same spot would fire on every single point placed, not just
  // the occasional stray click.
  'georeference',
] as const;

/** Whether the open tool wants the map's clicks to itself. */
export function toolOwnsMapClicks(toolId?: string | null): boolean {
  return Boolean(toolId) && (CLICK_OWNING_TOOL_IDS as readonly string[]).includes(toolId as string);
}

/** Tools whose panel is still written inline in TableOfContents. */
export const INLINE_TOOL_IDS = [
  'draw',
  'print',
  'measure',
  'filter',
  'query',
  'select',
  'assetSearch',
  'bookmark',
] as const;

/** Whether a tool id has a panel at all — in this folder or inline. */
export function hasToolPanel(toolId: string): boolean {
  return toolId in PANELS || (INLINE_TOOL_IDS as readonly string[]).includes(toolId);
}

/** Renders a tool's panel, or null when the id belongs to an inline one. */
export function renderToolPanel(toolId: string, ctx: ToolContext) {
  const Panel = PANELS[toolId];
  return Panel ? <Panel ctx={ctx} /> : null;
}
